import { useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { useLive } from "../lib/use-live";
import {centsToInput, money, parseMoney} from "../lib/format";
import { useRelative, useDateTime } from "../lib/use-relative";
import { useToast } from "../context/toast";
import { useT } from "../context/locale";
import {
  ActionButton,
  ActionIcon,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  LoadingRows,
  Modal,
  Panel,
  RowActions,
  Table,
  Td,
  Tr,
} from "../components/ui";
import { MoneyField, TextAreaField } from "../components/ui/form";
import { PageBar, SortHeader, useSort } from "../components/ui/filters";
import type { AdminUser, Paged } from "../types/api";

export function ReviewQueue() {
  const dateTime = useDateTime();
  const t = useT();
  const relative = useRelative();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const sort = useSort("createdAt", "desc", () => setPage(1));

  const queue = useLive<Paged<AdminUser>>("review-queue", {
        status: "pending_review",
        page,
        perPage,
        sort: sort.sort,
        order: sort.order,
      });

  const [approving, setApproving] = useState<AdminUser | null>(null);
  const [rejecting, setRejecting] = useState<AdminUser | null>(null);

  return (
    <div className="space-y-5">
      <Panel
        eyebrow={t("section.people")}
        title={t("review.title")}
        bodyClassName=""
        actions={
          <Button size="sm" onClick={queue.reload}>
            Refresh
          </Button>
        }
      >
        {queue.error ? (
          <div className="p-5">
            <ErrorNote message={queue.error} onRetry={queue.reload} />
          </div>
        ) : (
          <Table
            minWidth="72rem"
            head={[
              <SortHeader key="u" field="username" state={sort}>
                {t("common.player")}
              </SortHeader>,
              <SortHeader key="n" field="fullName" state={sort}>
                {t("common.fullName")}
              </SortHeader>,
              t("common.referralCode"),
              t("common.phone"),
              t("field.bankName"),
              t("field.bankAccountNo"),
              <SortHeader key="c" field="createdAt" state={sort}>
                {t("col.registered")}
              </SortHeader>,
              <span key="a" className="block text-right">
                {t("col.actions")}
              </span>,
            ]}
          >
            {queue.loading ? (
              <LoadingRows columns={8} />
            ) : queue.data && queue.data.items.length === 0 ? (
              <Tr>
                <Td colSpan={8}>
                  <EmptyState
                    title={t("review.empty")}
                    detail={t("review.emptyDetail")}
                  />
                </Td>
              </Tr>
            ) : (
              (queue.data?.items ?? []).map((user) => (
                <Tr key={user.id}>
                  <Td>
                    <Link
                      to={`/players/${user.id}`}
                      className="font-semibold hover:underline"
                    >
                      {user.username}
                    </Link>
                  </Td>
                  <Td className="text-muted">{user.fullName}</Td>
                  <Td>
                    {/* Which code brought them in — useful context when deciding
                        whether to approve, since codes map to a channel. */}
                    {user.referralCode ? (
                      <Link to={`/referrals?q=${user.referralCode.code}`}>
                        <Badge tone="gold">{user.referralCode.code}</Badge>
                      </Link>
                    ) : (
                      <span className="text-dim text-[12px]">—</span>
                    )}
                  </Td>
                  <Td className="money text-dim text-[12px]">
                    {user.phone ?? "—"}
                  </Td>
                  {/* The bank details the applicant typed. An approver checks
                      them against whatever the deposit came in under, so they
                      belong on the row being decided rather than one click
                      away. */}
                  <Td className="text-dim text-[12px]">{user.bankName ?? "—"}</Td>
                  <Td className="money text-dim text-[12px]">
                    {user.bankAccountNo ?? "—"}
                  </Td>
                  <Td
                    className="text-dim text-[12px]"
                    title={dateTime(user.createdAt)}
                  >
                    {relative(user.createdAt)}
                  </Td>
                  <Td>
                    <RowActions>
                      <ActionButton
                        icon={ActionIcon.reject}
                        tone="danger"
                        onClick={() => setRejecting(user)}
                      >
                        {t("review.reject")}
                      </ActionButton>
                      <ActionButton
                        icon={ActionIcon.approve}
                        tone="primary"
                        onClick={() => setApproving(user)}
                      >
                        {t("review.approve")}
                      </ActionButton>
                    </RowActions>
                  </Td>
                </Tr>
              ))
            )}
          </Table>
        )}
      </Panel>

      {queue.data ? (
        <div className="panel">
          <PageBar
            page={queue.data.page}
            pages={queue.data.pages}
            total={queue.data.total}
            perPage={perPage}
            onPage={setPage}
            onPerPage={(size) => {
              setPerPage(size);
              setPage(1);
            }}
          />
        </div>
      ) : null}

      <ApproveDialog
        user={approving}
        onClose={() => setApproving(null)}
        onDone={(username) => {
          toast.success(
            t("review.approvedToast"),
            t("review.approvedDetail", { name: username }),
          );
          queue.reload();
        }}
      />
      <RejectDialog
        user={rejecting}
        onClose={() => setRejecting(null)}
        onDone={(username) => {
          toast.success(
            t("review.rejectedToast"),
            t("review.rejectedDetail", { name: username }),
          );
          queue.reload();
        }}
      />
    </div>
  );
}

function ApproveDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onDone: (username: string) => void;
}) {
  const t = useT();
  const [amount, setAmount] = useState("0.00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = parseMoney(amount);
  const invalid = cents === null || cents < 0;

  async function submit() {
    if (!user || invalid) return;
    setBusy(true);
    setError(null);
    try {
      await api.approveUser(user.id, cents);
      onDone(user.username);
      onClose();
      setAmount("0.00");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("review.approveError"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      eyebrow={t("section.review")}
      title={t("review.approveTitle", { name: user?.username ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={busy}
            disabled={invalid}
          >
            {t("review.approveSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">
          {t("review.approveBlurb", {
            name: user?.fullName ?? t("review.thisPlayer"),
          })}
        </p>

        <MoneyField
          label={t("field.openingBalance")}
          value={amount}
          onChange={setAmount}
          hint={
            cents !== null && cents > 0
              ? t("review.creditsOnApproval", { amount: money(cents) })
              : t("review.noOpening")
          }
          error={invalid ? t("credit.nonNegative") : null}
        />

        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

function RejectDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onDone: (username: string) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!user || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.rejectUser(user.id, reason.trim());
      onDone(user.username);
      onClose();
      setReason("");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t("review.rejectError"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      eyebrow={t("section.review")}
      title={t("review.rejectTitle", { name: user?.username ?? "" })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("action.cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={submit}
            loading={busy}
            disabled={!reason.trim()}
          >
            {t("review.rejectSubmit")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted text-[13px]">{t("review.rejectBlurb")}</p>
        <TextAreaField
          label={t("common.reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("review.rejectPlaceholder")}
          required
        />
        {error ? <ErrorNote message={error} /> : null}
      </div>
    </Modal>
  );
}

export { centsToInput };
